import React, { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Check, X, Loader2 } from "lucide-react";

export default function CheckoutResult({ success }) {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("pending");
  const [attempts, setAttempts] = useState(0);
  const pollRef = useRef(null);

  useEffect(() => {
    if (!success || !sessionId) return;
    let cancelled = false;
    const poll = async (n = 0) => {
      if (cancelled) return;
      if (n >= 8) { setStatus("timeout"); return; }
      try {
        const { data } = await api.get(`/vip/status/${sessionId}`);
        if (data.payment_status === "paid") { setStatus("paid"); return; }
        if (data.status === "expired") { setStatus("expired"); return; }
        setAttempts(n + 1);
        pollRef.current = setTimeout(() => poll(n + 1), 2000);
      } catch {
        setStatus("error"); 
      }
    };
    poll(0);
    return () => { cancelled = true; if (pollRef.current) clearTimeout(pollRef.current); };
  }, [sessionId, success]);

  if (!success) {
    return (
      <div className="max-w-lg mx-auto pt-24 pb-24 px-6 text-center fade-up" data-testid="checkout-cancel">
        <X size={56} strokeWidth={1} color="var(--text-secondary)" className="mx-auto mb-4" />
        <h1 className="font-serif text-4xl mb-3" style={{ color: "var(--text-primary)" }}>Pago cancelado</h1>
        <p className="mb-8" style={{ color: "var(--text-secondary)" }}>No se realizó ningún cargo. Puedes intentarlo de nuevo cuando gustes.</p>
        <Link to="/dashboard" className="btn-primary">Volver al panel</Link>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto pt-24 pb-24 px-6 text-center fade-up" data-testid="checkout-success">
      {status === "pending" && (
        <>
          <Loader2 size={56} strokeWidth={1} color="var(--brand-primary)" className="mx-auto mb-4 animate-spin" />
          <h1 className="font-serif text-4xl mb-3" style={{ color: "var(--text-primary)" }}>Procesando pago…</h1>
          <p style={{ color: "var(--text-secondary)" }}>Verificando tu transacción (intento {attempts + 1}).</p>
        </>
      )}
      {status === "paid" && (
        <>
          <Check size={56} strokeWidth={1} color="var(--vip-gold)" className="mx-auto mb-4" />
          <h1 className="font-serif text-4xl mb-3" style={{ color: "var(--vip-gold)" }}>¡Bienvenida al club VIP!</h1>
          <p className="mb-8" style={{ color: "var(--text-secondary)" }}>Tu perfil ahora aparece destacado con el sello dorado.</p>
          <Link to="/dashboard" className="btn-primary" data-testid="checkout-return">Ir a mi panel</Link>
        </>
      )}
      {status === "expired" && (
        <>
          <X size={56} strokeWidth={1} color="#ef6d6d" className="mx-auto mb-4" />
          <h1 className="font-serif text-4xl mb-3" style={{ color: "var(--text-primary)" }}>Sesión expirada</h1>
          <Link to="/dashboard" className="btn-secondary">Intentar de nuevo</Link>
        </>
      )}
      {(status === "timeout" || status === "error") && (
        <>
          <p className="mb-4" style={{ color: "var(--text-secondary)" }}>
            {status === "timeout" ? "Tiempo de verificación agotado." : "Error al verificar el pago."}
          </p>
          <Link to="/dashboard" className="btn-secondary">Volver al panel</Link>
        </>
      )}
    </div>
  );
}
